import { addDays, endOfDay, isAfter, isBefore, isSameDay, isWithinInterval, startOfDay } from 'date-fns'

export const attachmentTypeMap = {
  ABNDocument: 'ABNDocument',
  ProofOfBankDetails: 'ProofOfBankDetails',
  IncorporationFile: 'IncorporationFile',
  ParentOrganizationLetter: 'ParentOrganizationLetter',
  StatutoryDeclaration: 'StatutoryDeclaration'
}

export const attachmentLabelMap = {
  [attachmentTypeMap.ABNDocument]: 'Copy of ABN',
  [attachmentTypeMap.ProofOfBankDetails]: 'Proof of bank details',
  [attachmentTypeMap.IncorporationFile]: 'Incorporation file',
  [attachmentTypeMap.ParentOrganizationLetter]: 'Parent organisation letter',
  [attachmentTypeMap.StatutoryDeclaration]: 'Statutory declaration'
}

export const charityStatusMap: { [K in CharityStatusType]: string } = {
  NEW: 'New',
  ACTIVE: 'Active',
  DISABLED: 'Disabled'
}

export const readableCharityFieldMap = {
  id: 'ID',
  status: 'Status',
  createdAt: 'Created at',
  administratorUserId: 'Admin ID',
  administratorEmail: 'Admin email',
  logoUrl: 'Logo url',
  barcode: 'Barcode',
  updatedAt: 'Updated at',
  updatedBy: 'Updated by',
  organization: {
    name: 'Name',
    organizationAbn: 'ABN',
    appContext: 'App',
    registeredState: 'Registered in',
    establishedDate: 'Established date',
    legalStructure: 'Legal structure',
    legalStructureDetails: 'Legal structure details',
    geoFootprint: 'Operational footprint',
    incorporationNumber: 'Incorporation number',
    displayName: 'Display name',
    localGroupName: 'Local group name',
    streetAddress: 'Street address',
    postCode: 'Postcode',
    suburb: 'Suburb',
    benefit: 'Benefit',
    websiteUrl: 'Website URL',
    description: 'Description',
    externalId: 'Donation Partner ID'
  },
  bankingData: {
    accountName: 'Account name',
    accountBsb: 'BSB',
    accountNumber: 'Account number',
    dgrStatus: 'DGR status'
  },
  primaryContact: {
    firstName: 'First name',
    lastName: 'Last name',
    position: 'Position',
    email: 'Email',
    phone: 'Phone'
  },
  secondaryContact: {
    firstName: 'First name',
    lastName: 'Last name',
    position: 'Position',
    email: 'Email',
    phone: 'Phone'
  }
}

export function getFilterFromSearchParams(searchParams: URLSearchParams): CharityFilterType {
  const entries = Object.fromEntries(searchParams)

  return Object.entries(entries).reduce((acc: CharityFilterType, [key, value]) => {
    if (value.includes(',')) {
      const parsedValue = value.split(',')
      return { ...acc, [key]: parsedValue }
    }

    return { ...acc, [key]: value }
  }, {} as CharityFilterType)
}

export function filterCharities(data: CharityReportType[], filter: CharityFilterType) {
  let filtered: CharityReportType[] = data

  if (filter.name) {
    const nameFilter = filter.name.trim().toLowerCase()
    filtered = filtered.filter(filterByName(nameFilter))
  }

  if (filter.status) {
    filtered = filtered.filter(filterByCharityStatus(filter.status))
  }

  if (filter.campaignStatus) {
    filtered = filtered.filter(filterByCampaignStatus(filter.campaignStatus))
  }

  if (filter.startTime && filter.endTime) {
    filtered = filtered.filter(filterByDateRange(filter.startTime, filter.endTime))
  } else {
    if (filter.startTime) {
      filtered = filtered.filter(filterByCampaignStartTime(filter.startTime))
    }

    if (filter.endTime) {
      filtered = filtered.filter(filterByCampaignEndTime(filter.endTime))
    }
  }

  return filtered
}

function filterByName(nameFilter: string) {
  return (data: CharityReportType) => {
    const displayName = data.charity.organization.displayName?.trim().toLowerCase() || ''
    const organizationName = data.charity.organization.name?.trim().toLowerCase() || ''
    return displayName.includes(nameFilter) || organizationName.includes(nameFilter)
  }
}

function filterByCharityStatus(status: CharityStatusType[] | string) {
  return (data: CharityReportType) => {
    return status.includes(data.charity.status) || (status as string).includes(data.charity.note)
  }
}

function filterByCampaignStatus(campaignStatus: (CampaignStatusType | 'NONE')[]) {
  return (data: CharityReportType) => {
    if (!data.campaign) {
      return campaignStatus.includes('NONE')
    }

    return campaignStatus.includes(data.campaign.status) || (campaignStatus.includes('NONE') && !data.campaign.status)
  }
}

function filterByCampaignStartTime(date: string) {
  return (data: CharityReportType) => {
    if (!data.campaign) {
      return false
    }

    const filterDate = new Date(date)
    const campaignStartTime = new Date(data.campaign.startTime)
    return isSameDay(campaignStartTime, filterDate) || isAfter(campaignStartTime, filterDate)
  }
}

function filterByCampaignEndTime(date: string) {
  return (data: CharityReportType) => {
    if (!data.campaign) {
      return false
    }

    const filterDate = new Date(date)
    const campaignEndTime = new Date(data.campaign.endTime)

    return isSameDay(campaignEndTime, filterDate) || isBefore(campaignEndTime, filterDate)
  }
}

function filterByDateRange(startDate: string, endDate: string) {
  return (data: CharityReportType) => {
    if (!data.campaign) {
      return false
    }

    const startDateParsed = new Date(startDate)
    const endDateParsed = new Date(endDate)

    // In date-fns v4, invalid intervals (where start > end) are now handled differently
    // Check if we have an invalid date range and handle it by returning true (skip filtering)
    // This maintains backwards compatibility with the previous behavior
    if (startDateParsed > endDateParsed) {
      return true
    }

    try {
      const interval = {
        start: startOfDay(startDateParsed),
        end: addDays(endOfDay(endDateParsed), 1)
      }

      const campaignStart = startOfDay(new Date(data.campaign.startTime))
      const campaignEnd = endOfDay(new Date(data.campaign.endTime))

      return isWithinInterval(campaignStart, interval) && isWithinInterval(campaignEnd, interval)
    } catch {
      // Invalid date range, just skip filter
      return true
    }
  }
}

export function mergeCharityAndCampaignData(
  newCharities: CharitySummaryType[],
  updatedCharities: CharitySummaryType[],
  newCampaigns: CampaignSummaryType[],
  updatedCampaigns: CampaignSummaryType[]
) {
  const mergedData: {
    [key: string]: CharitySummaryType & { campaigns: CampaignSummaryType[] }
  } = {}

  newCharities.forEach(charity => {
    mergedData[charity.id] = { ...charity, campaigns: [] }
  })

  updatedCharities.forEach(charity => {
    if (mergedData[charity.id]) {
      mergedData[charity.id] = { ...mergedData[charity.id], ...charity }
    } else {
      mergedData[charity.id] = { ...charity, campaigns: [] }
    }
  })

  newCampaigns.forEach(campaign => {
    if (mergedData[campaign.charitySummary.id]) {
      mergedData[campaign.charitySummary.id].campaigns.push(campaign)
    } else {
      mergedData[campaign.charitySummary.id] = {
        ...campaign.charitySummary,
        campaigns: [campaign]
      }
    }
  })

  updatedCampaigns.forEach(campaign => {
    if (mergedData[campaign.charitySummary.id]) {
      const existingCampaignIndex = mergedData[campaign.charitySummary.id].campaigns.findIndex(
        c => c.id === campaign.id
      )

      if (existingCampaignIndex > -1) {
        mergedData[campaign.charitySummary.id].campaigns[existingCampaignIndex] = {
          ...mergedData[campaign.charitySummary.id].campaigns[existingCampaignIndex],
          ...campaign
        }
      } else {
        mergedData[campaign.charitySummary.id].campaigns.push(campaign)
      }
    } else {
      mergedData[campaign.charitySummary.id] = {
        ...campaign.charitySummary,
        campaigns: [campaign]
      }
    }
  })

  return Object.values(mergedData)
}
